import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject, forkJoin } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { MicrosoftCalendarService, MicrosoftCalendarEvent } from './microsoft-calendar.service';

export interface CalendarAuthResponse {
  type: 'GOOGLE_AUTH_SUCCESS' | 'GOOGLE_AUTH_ERROR';
  userId?: string;
  user?: {
    id: string;
    email: string;
    name: string;
    picture?: string;
    given_name?: string;
    family_name?: string;
    verified_email?: boolean;
  };
  tokens?: {
    access_token: string;
    refresh_token: string;
    scope: string;
    token_type: string;
    expires_in: number;
    expiry_date: number;
  };
  error?: string;
}

export interface CalendarConnection {
  id: number;
  provider: string;
  user: {
    id: string;
    email: string;
    name: string;
    picture?: string;
  };
  scopes: string[];
  connected_at: string;
  last_sync_at?: string;
  token_expires_at: string;
  token_expiring?: boolean;
}

export interface CalendarEvent {
  id: number;
  external_id: string;
  calendar_id: string;
  title: string;
  description?: string;
  location?: string;
  status: string;
  start: {
    date?: string;
    dateTime?: string;
    timeZone?: string;
    allDay?: boolean;
  };
  end: {
    date?: string;
    dateTime?: string;
    timeZone?: string;
    allDay?: boolean;
  };
  attendees?: any[];
  organizer?: any;
  html_link?: string;
  requirement_id?: number;
  created_at: string;
  updated_at: string;
  synced_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class CalendarIntegrationService {
  private authSubject = new Subject<CalendarAuthResponse>();
  private authWindow: Window | null = null;
  private connectionSubject = new BehaviorSubject<CalendarConnection | null>(null);

  public connection$ = this.connectionSubject.asObservable();

  constructor(
    private http: HttpClient,
    private microsoftCalendarService: MicrosoftCalendarService
  ) {
    console.log('🔧 CalendarIntegrationService inicializado');

    // Escuchar mensajes de la ventana popup
    window.addEventListener('message', this.handleAuthMessage.bind(this));
    console.log('👂 Listener de postMessage registrado');

    // Verificar conexión al iniciar
    this.checkConnection();
  }

  /**
   * Verificar si hay conexión activa
   */
  private checkConnection(): void {
    console.log('🔍 Verificando conexiones con backend...');

    // Verificar Google Calendar
    this.getConnection().subscribe({
      next: (response: any) => {
        console.log('📥 Respuesta del backend para Google:', response);

        // El backend de Inprovider envuelve la respuesta en "result"
        const data = response.result || response;

        if (data.connected) {
          console.log('✅ Usuario CONECTADO a Google Calendar:', data.connection);
          this.connectionSubject.next(data.connection);
        } else {
          console.log('❌ Usuario NO conectado a Google Calendar');
          this.connectionSubject.next(null);
        }
      },
      error: (error) => {
        console.error('❌ Error al verificar conexión Google:', error);
        this.connectionSubject.next(null);
      }
    });

    // Verificar Microsoft Calendar
    this.checkMicrosoftConnection();
  }

  private checkMicrosoftConnection(): void {
    console.log('🔍 Verificando conexión Microsoft...');
    this.http.get(`${environment.apiUrl}/common/calendario/get/microsoft`).subscribe({
      next: (response: any) => {
        console.log('📥 Respuesta del backend para Microsoft:', response);
        const data = response.result || response;

        if (data.connected) {
          console.log('✅ Usuario CONECTADO a Microsoft Calendar:', data.connection);
          // Actualizar el estado de Microsoft en el servicio
          this.microsoftCalendarService.updateUser(data.connection);
        } else {
          console.log('❌ Usuario NO conectado a Microsoft Calendar');
        }
      },
      error: (error) => {
        console.error('❌ Error al verificar conexión Microsoft:', error);
      }
    });
  }

  /**
   * Conectar Google Calendar
   */
  connectGoogleCalendar(): Observable<any> {
    return this.http.post(`${environment.apiUrl}/common/calendario/connect`, {
      provider: 'google'
    }).pipe(
      tap((response: any) => {
        console.log('✅ Connect response:', response);
      }),
      catchError(error => {
        console.error('❌ Error connecting:', error);
        throw error;
      })
    );
  }

  /**
   * Abrir popup de autenticación
   */
  openAuthPopup(authUrl: string): Observable<CalendarAuthResponse> {
    const width = 500;
    const height = 600;
    const left = (screen.width / 2) - (width / 2);
    const top = (screen.height / 2) - (height / 2);

    console.log('🪟 Abriendo popup:', authUrl);

    this.authWindow = window.open(
      authUrl,
      'Google Calendar Authentication',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
    );

    // MÉTODO 1: Polling de localStorage (más confiable)
    console.log('🔄 Iniciando polling de localStorage...');
    const checkAuth = setInterval(() => {
      const authResult = localStorage.getItem('google_calendar_auth_result');

      if (authResult) {
        try {
          const data = JSON.parse(authResult);

          // Verificar que sea reciente (menos de 30 segundos)
          if (data.timestamp && (Date.now() - data.timestamp < 30000)) {
            clearInterval(checkAuth);
            localStorage.removeItem('google_calendar_auth_result');

            console.log('✅ Datos recuperados de localStorage:', data);
            console.log('💾 Guardando tokens en backend...');

            // Guardar tokens en el backend
            this.saveTokens(data.tokens, data.user).subscribe({
              next: (response) => {
                console.log('✅ Tokens guardados exitosamente:', response);
                this.connectionSubject.next(response.connection);
                this.authSubject.next(data);
              },
              error: (error) => {
                console.error('❌ Error guardando tokens:', error);
                this.authSubject.error(error);
              }
            });
          } else {
            console.log('⚠️ authResult expirado, limpiando...');
            localStorage.removeItem('google_calendar_auth_result');
          }
        } catch (e) {
          console.error('❌ Error parseando authResult:', e);
          localStorage.removeItem('google_calendar_auth_result');
        }
      }

      // Verificar si la ventana se cerró
      if (this.authWindow && this.authWindow.closed) {
        console.log('🔒 Popup cerrado');
        clearInterval(checkAuth);
        this.authWindow = null;

        // Dar tiempo extra por si el localStorage se guardó justo antes de cerrar
        setTimeout(() => {
          const finalCheck = localStorage.getItem('google_calendar_auth_result');
          if (finalCheck) {
            console.log('⚠️ Datos encontrados después de cerrar popup, procesando...');
            // Trigger manual del evento
            const event = new StorageEvent('storage', {
              key: 'google_calendar_auth_result',
              newValue: finalCheck
            });
            window.dispatchEvent(event);
          }
        }, 500);
      }
    }, 500);

    return this.authSubject.asObservable();
  }

  /**
   * Manejar mensaje del popup de autenticación
   */
  private handleAuthMessage(event: MessageEvent) {
    console.log('📨 Mensaje recibido:', event);
    console.log('📨 Origin:', event.origin);
    console.log('📨 Data:', event.data);

    // Verificar origen (en desarrollo permitir localhost)
    if (!event.origin.includes('localhost') && !event.origin.includes(window.location.hostname)) {
      console.warn('⚠️ Mensaje ignorado por origen no válido:', event.origin);
      return;
    }

    const data: CalendarAuthResponse = event.data;

    // Verificar que sea un mensaje de autenticación
    if (!data || !data.type) {
      console.log('⚠️ Mensaje ignorado (no es de autenticación)');
      return;
    }

    if (data.type === 'GOOGLE_AUTH_SUCCESS') {
      console.log('✅ Autenticación exitosa recibida:', data);

      // Guardar tokens en el backend
      console.log('💾 Guardando tokens en el backend...');
      this.saveTokens(data.tokens!, data.user!)
        .subscribe({
          next: (response) => {
            console.log('✅ Tokens guardados correctamente:', response);
            this.connectionSubject.next(response.connection);
            this.authSubject.next(data);
          },
          error: (error) => {
            console.error('❌ Error guardando tokens:', error);
            this.authSubject.error(error);
          }
        });

    } else if (data.type === 'GOOGLE_AUTH_ERROR') {
      console.error('❌ Error en autenticación:', data.error);
      this.authSubject.error(data.error);
    }
  }

  /**
   * Guardar tokens en el backend de Inprovider
   */
  saveTokens(tokens: any, user: any, provider?: string): Observable<any> {
    // Detectar automáticamente el proveedor basado en los tokens
    let detectedProvider = provider;

    if (!detectedProvider) {
      if (tokens.accessToken && tokens.idToken) {
        // Microsoft tokens tienen accessToken e idToken
        detectedProvider = 'microsoft';
      } else if (tokens.access_token) {
        // Google tokens tienen access_token
        detectedProvider = 'google';
      } else {
        detectedProvider = 'unknown';
      }
    }

    // Transformar tokens al formato esperado por el backend
    let formattedTokens = tokens;
    if (detectedProvider === 'microsoft') {
      // Convertir Microsoft tokens al formato esperado por el backend
      formattedTokens = {
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken || 'no-refresh-token-available',
        id_token: tokens.idToken,
        expires_in: tokens.expiresOn ? Math.floor((new Date(tokens.expiresOn).getTime() - Date.now()) / 1000) : 3600,
        expiry_date: tokens.expiresOn ? new Date(tokens.expiresOn).getTime() : Date.now() + 3600000, // Timestamp de expiración
        token_type: 'Bearer',
        scope: tokens.scopes ? tokens.scopes.join(' ') : ''
      };
    }

    // Formatear usuario según el proveedor
    let formattedUser = user;
    if (detectedProvider === 'microsoft') {
      // Mapear campos de Microsoft al formato esperado por el backend
      formattedUser = {
        id: user.id,
        name: user.displayName,
        email: user.mail || user.userPrincipalName,
        given_name: user.givenName,
        family_name: user.surname,
        picture: user.picture || null
      };
    }

    console.log('💾 Guardando tokens para proveedor:', detectedProvider);
    console.log('📦 Tokens formateados:', formattedTokens);
    console.log('👤 Usuario formateado:', formattedUser);

    return this.http.post(`${environment.apiUrl}/common/calendario/save-tokens`, {
      provider: detectedProvider,
      tokens: formattedTokens,
      user: formattedUser
    }).pipe(
      tap((response: any) => {
        console.log('💾 Tokens guardados, actualizando estado de conexión...');
        // El backend envuelve la respuesta en "result"
        const data = response.result || response;
        if (data.success && data.connection) {
          this.connectionSubject.next(data.connection);
        }
      })
    );
  }

  /**
   * Obtener información de la conexión
   */
  getConnection(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/common/calendario/get/google`);
  }

  /**
   * Obtener eventos del calendario
   */
  getEvents(startDate: Date, endDate: Date, sync = false): Observable<any> {
    console.log('📅 Solicitando eventos de Google Calendar:', {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      sync
    });

    return this.http.get(`${environment.apiUrl}/common/calendario/events`, {
      params: {
        provider: 'google',
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        sync: sync.toString()
      }
    }).pipe(
      tap((response: any) => {
        console.log('📥 Respuesta de eventos Google:', response);
        // El backend envuelve la respuesta en "result"
        const data = response.result || response;
        console.log('📊 Eventos Google recibidos:', data.events ? data.events.length : 0);
      })
    );
  }

  /**
   * Obtener eventos de Microsoft Calendar
   */
  getMicrosoftEvents(startDate: Date, endDate: Date, sync = false): Observable<any> {
    console.log('📅 Solicitando eventos de Microsoft Calendar:', {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      sync
    });

    return this.http.get(`${environment.apiUrl}/common/calendario/events`, {
      params: {
        provider: 'microsoft',
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        sync: sync.toString()
      }
    }).pipe(
      tap((response: any) => {
        console.log('📥 Respuesta de eventos Microsoft:', response);
        // El backend envuelve la respuesta en "result"
        const data = response.result || response;
        console.log('📊 Eventos Microsoft recibidos:', data.events ? data.events.length : 0);
      })
    );
  }

  /**
   * Obtener eventos de ambos proveedores (Google y Microsoft)
   */
  getAllEvents(startDate: Date, endDate: Date, sync = false): Observable<any> {
    console.log('📅 Solicitando eventos de ambos calendarios:', {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      sync
    });

    // Combinar observables de ambos proveedores
    return forkJoin({
      google: this.getEvents(startDate, endDate, sync),
      microsoft: this.getMicrosoftEvents(startDate, endDate, sync)
    }).pipe(
      map((responses: any) => {
        console.log('📥 Respuestas combinadas sin procesar:', responses);

        // Procesar respuesta de Google
        let googleData = responses.google;
        if (googleData && googleData.result) {
          googleData = googleData.result;
        }
        console.log('📊 Datos de Google procesados:', googleData);
        const googleEvents = (googleData && googleData.events && Array.isArray(googleData.events)) 
          ? googleData.events 
          : [];
        console.log('📅 Eventos de Google extraídos:', googleEvents.length);

        // Procesar respuesta de Microsoft
        let microsoftData = responses.microsoft;
        if (microsoftData && microsoftData.result) {
          microsoftData = microsoftData.result;
        }
        console.log('📊 Datos de Microsoft procesados:', microsoftData);
        const microsoftEvents = (microsoftData && microsoftData.events && Array.isArray(microsoftData.events)) 
          ? microsoftData.events 
          : [];
        console.log('📅 Eventos de Microsoft extraídos:', microsoftEvents.length);

        // Combinar todos los eventos
        const allEvents = [...googleEvents, ...microsoftEvents];

        // Ordenar por fecha de inicio
        allEvents.sort((a, b) => {
          const dateA = a.start?.dateTime || a.start?.date;
          const dateB = b.start?.dateTime || b.start?.date;
          if (!dateA || !dateB) return 0;
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        });

        console.log('📊 Total eventos combinados:', allEvents.length);
        console.log('📊 Google:', googleEvents.length, 'Microsoft:', microsoftEvents.length);

        return {
          success: true,
          events: allEvents,
          google: {
            events: googleEvents,
            connection: googleData.connection,
            synced: googleData.synced
          },
          microsoft: {
            events: microsoftEvents,
            connection: microsoftData.connection,
            synced: microsoftData.synced
          },
          total: allEvents.length,
          range: {
            start: startDate.toISOString(),
            end: endDate.toISOString()
          }
        };
      }),
      tap((combinedResponse: any) => {
        console.log('📊 Respuesta final combinada:', combinedResponse);
      })
    );
  }

  /**
   * Sincronizar eventos
   */
  syncEvents(startDate: Date, endDate: Date): Observable<any> {
    return this.http.post(`${environment.apiUrl}/common/calendario/sync`, {
      provider: 'google',
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString()
    });
  }

  /**
   * Desconectar calendario
   */
  disconnect(): Observable<any> {
    return this.http.request('delete', `${environment.apiUrl}/common/calendario/disconnect`, {
      body: {
        provider: 'google',
        delete_events: true
      }
    }).pipe(
      tap(() => {
        this.connectionSubject.next(null);
      })
    );
  }

  /**
   * Obtener estado de conexión actual
   */
  getCurrentConnection(): CalendarConnection | null {
    return this.connectionSubject.value;
  }

  /**
   * Verificar si está conectado
   */
  isConnected(): boolean {
    // Verificar tanto el estado local como el backend
    const localConnected = this.connectionSubject.value !== null;
    console.log('🔌 isConnected() llamado:', localConnected, 'Value:', this.connectionSubject.value);

    // Si hay estado local, usarlo; si no, asumir que no está conectado
    // El estado real se verificará en el backend cuando se haga la petición
    return localConnected;
  }

  /**
   * Reiniciar el estado del servicio (útil al cambiar de usuario)
   */
  public resetService(): void {
    console.log('🔄 Reiniciando CalendarIntegrationService...');
    this.connectionSubject.next(null);
    this.authSubject = new Subject<CalendarAuthResponse>();

    // Limpiar localStorage
    localStorage.removeItem('google_calendar_connection');
    localStorage.removeItem('google_calendar_auth_result');

    // Limpiar conexión de Microsoft
    this.microsoftCalendarService.clearConnection();

    console.log('✅ Servicio reiniciado');
  }

  // ==================== MÉTODOS DE MICROSOFT CALENDAR ====================

  /**
   * Conectar a Microsoft Calendar
   */
  connectMicrosoft(): void {
    this.microsoftCalendarService.connect();
  }

  /**
   * Verificar si está conectado a Microsoft Calendar
   */
  isMicrosoftConnected(): boolean {
    return this.microsoftCalendarService.isConnected();
  }

  /**
   * Verificar conexión de Microsoft desde el backend
   */
  checkMicrosoftConnectionFromBackend(): Observable<any> {
    console.log('🔍 Verificando conexión Microsoft desde backend...');
    return this.http.get(`${environment.apiUrl}/common/calendario/get/microsoft`).pipe(
      tap((response: any) => {
        console.log('📥 Respuesta del backend para Microsoft:', response);
        const data = response.result || response;

        if (data.connected) {
          console.log('✅ Usuario CONECTADO a Microsoft Calendar:', data.connection);
          // Actualizar el estado de Microsoft en el servicio
          this.microsoftCalendarService.updateUser(data.connection);
        } else {
          console.log('❌ Usuario NO conectado a Microsoft Calendar');
        }
      })
    );
  }

  /**
   * Eliminar conexión del backend (Google)
   */
  deleteGoogleConnection(): Observable<any> {
    console.log('🗑️ Eliminando conexión de Google del backend...');
    return this.http.delete(`${environment.apiUrl}/common/calendario/delete?provider=google`).pipe(
      tap((response: any) => {
        console.log('✅ Conexión de Google eliminada del backend:', response);
      })
    );
  }

  /**
   * Eliminar conexión del backend (Microsoft)
   */
  deleteMicrosoftConnection(): Observable<any> {
    console.log('🗑️ Eliminando conexión de Microsoft del backend...');
    return this.http.delete(`${environment.apiUrl}/common/calendario/delete?provider=microsoft`).pipe(
      tap((response: any) => {
        console.log('✅ Conexión de Microsoft eliminada del backend:', response);
      })
    );
  }

  /**
   * Desconectar de Microsoft Calendar
   */
  disconnectMicrosoft(): Observable<boolean> {
    return this.microsoftCalendarService.disconnect();
  }

  /**
   * Obtener información del usuario de Microsoft
   */
  getMicrosoftUser(): any {
    return this.microsoftCalendarService.getConnectedUser();
  }

  /**
   * Manejar callback de Microsoft
   */
  handleMicrosoftCallback(): Observable<{ success: boolean; user?: any; error?: string }> {
    return this.microsoftCalendarService.handleCallback();
  }
}

